import {
  Accordion,
  AccordionButton,
  AccordionIcon,
  AccordionItem,
  AccordionPanel,
  Card,
  Flex,
  HStack,
  Icon,
  Skeleton,
  Stack,
  Text
} from '@chakra-ui/react';
import { CycleIcon, HistoryIcon } from 'assets/icons/miscs';
import { ReactComponent as PrepaidIcon } from 'assets/icons/prepaid.svg';
import LabeledIconButton from 'components/modules/LabeledIconButton';
import { FC } from 'react';

type GingerPrepaidAccordionProps = {
  prepaidDisplay: {
    prepaidName?: string | null;
    prepaidBalance?: number;
    prepaidUnit?: string | null;
  };
  isPrepaidMutating: boolean;
  onToggle: (expandedIndex: number) => void;
  onUpdateClick: () => void;
  isUpdateLoading: boolean;
  showHistoryButton: boolean;
  historyPath?: string;
};

/**
 * VD+CSD ハウスマネー表示アコーディオンコンポーネント
 *
 * @params
 * prepaidDisplay ハウスマネー表示に関する情報をまとめたObject
 *   prepaidName ハウスマネーセクションのタイトル テンプレート設定
 *   prepaidBalance ハウスマネー残高 APIから受け取り
 *   prepaidUnit ハウスマネーの単位 テンプレート設定
 * isPrepaidMutating ハウスマネー残高取得APIのmutate状態
 * onToggle アコーディオンの開閉時に呼ばれるコールバック
 * onUpdateClick 更新ボタンクリック時のハンドラ
 * isUpdateLoading 更新中の状態
 * showHistoryButton 履歴ボタンを表示するかどうか
 * historyPath 履歴ページへのパス
 */
const GingerPrepaidAccordion: FC<GingerPrepaidAccordionProps> = ({
  prepaidDisplay: { prepaidName, prepaidBalance, prepaidUnit },
  isPrepaidMutating,
  onToggle,
  onUpdateClick,
  isUpdateLoading,
  showHistoryButton,
  historyPath
}) => {
  return (
    <Card boxShadow="xs" overflow="hidden">
      <Accordion allowToggle onChange={onToggle}>
        <AccordionItem borderStyle="none">
          <AccordionButton justifyContent="space-between" padding="1rem">
            <HStack spacing="0.75rem" alignItems="center">
              <Icon as={PrepaidIcon} boxSize="2.25rem" />
              {!!prepaidName && (
                <Text
                  fontSize="md"
                  wordBreak="break-all"
                  whiteSpace="pre-wrap"
                  color="dark.400"
                >
                  {prepaidName}
                </Text>
              )}
            </HStack>
            <AccordionIcon />
          </AccordionButton>
          <AccordionPanel padding="0 1rem 1rem 1rem">
            <Flex
              m="0"
              w="100%"
              alignItems="center"
              justifyContent="space-between"
              gap="1.5rem"
            >
              <Stack gap="0.25rem">
                <Skeleton isLoaded={!isPrepaidMutating}>
                  <Text
                    fontSize="4xl"
                    fontWeight="bold"
                    data-testid="prepaid-element"
                  >
                    {prepaidBalance?.toLocaleString() ?? '-'}
                    {!!prepaidUnit && (
                      <Text
                        as="span"
                        fontSize="xl"
                        fontWeight="400"
                        wordBreak="break-all"
                        whiteSpace="pre-wrap"
                      >
                        {prepaidUnit}
                      </Text>
                    )}
                  </Text>
                </Skeleton>
              </Stack>
              {/* ボタンリスト */}
              <HStack gap="0.5rem">
                <LabeledIconButton
                  buttonProps={{
                    onClick: onUpdateClick,
                    isLoading: isUpdateLoading,
                    icon: <CycleIcon color="primary" boxSize="1.5rem" />,
                    borderColor: 'primary'
                  }}
                  textProps={{
                    content: '更新',
                    color: 'primary'
                  }}
                />
                {showHistoryButton && historyPath && (
                  <LabeledIconButton
                    buttonProps={{
                      to: historyPath,
                      icon: <HistoryIcon color="primary" boxSize="1.25rem" />,
                      borderColor: 'primary'
                    }}
                    textProps={{
                      content: '履歴',
                      color: 'primary'
                    }}
                  />
                )}
              </HStack>
            </Flex>
          </AccordionPanel>
        </AccordionItem>
      </Accordion>
    </Card>
  );
};

export default GingerPrepaidAccordion;
